import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import DOMPurify from 'dompurify'

import { observer } from 'mobx-react'

import createAutorun from 'modules/core/hooks/createAutorun'
import createComputed from 'modules/core/hooks/createComputed'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import Attachments from 'modules/bookkeepers/components/IncidenceInformation/Attachments'
import { useFiles } from 'modules/bookkeepers/store/collections/files'
import type { Files } from 'modules/bookkeepers/store/collections/files'
import type IncidenceMessage from 'modules/bookkeepers/store/models/IncidenceMessage'

interface Props {
  message: IncidenceMessage
}

const useFilteredAttachments = createComputed((files: Files, messageId: number) => {
  return files.filter((file) => file.attributes.incidence_message_id === messageId)
})

const useAutoScroll = createAutorun((ref) => {
  return ref.current?.scrollIntoView({ behavior: 'smooth' })
})

const MessageContent = ({ message }: Props) => {
  const filesEndRef = React.createRef<HTMLDivElement>()
  const files = useFiles()
  const date = stringToDate(message.attributes.created_at, 'backend_datetime')
  const attachments = useFilteredAttachments(files, message.id).get()

  useAutoScroll(filesEndRef)

  return (
    <Box width="full" gap="s2" overflowX="hidden">
      <Box flexDirection="row" gap="s8">
        <Box>
          <Text.Normal size="200" weight="semibold" color="grey1000">
            {message.attributes.sender_name ??
              i18n.t('bookkeepers.incidence.message.bot_sender_name')}
          </Text.Normal>
        </Box>
        <Box alignSelf="center">
          <Text.Normal size="100" weight="normal" color="grey600">
            {dateToString(date, 'ui_time')}
          </Text.Normal>
        </Box>
      </Box>
      <Text.Normal weight="normal" color="grey1000">
        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(message.attributes.message),
          }}
        />
      </Text.Normal>
      <Box paddingY="s8">{attachments.length > 0 && <Attachments files={attachments} />}</Box>
      <div ref={filesEndRef} />
    </Box>
  )
}

export default observer(MessageContent)
